// src/modules/stock/StockMovementModal.js

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { db } from '../../firebaseConfig';
import {
    collection,
    doc,
    runTransaction,
    serverTimestamp,
    getDocs,
    onSnapshot,
    query,
    where,
    writeBatch
} from 'firebase/firestore';
import useAuth from '../../hooks/useAuth';
import { withFirestoreRetry, CircuitBreaker } from '../../utils/retry';

// Import global components
import { FormModal, Toast } from '../../components/common';

import styles from './Logistics.module.css';

const CURRENT_STOCK_COLLECTION = 'current-stock';
const STOCK_OUTFLOWS_COLLECTION = 'stock-outflows';
const STOCK_ENTRIES_COLLECTION = 'stock-entries';
const LOCATIONS_COLLECTION = 'localizacoes';

// Circuit breaker for stock transfers
const transferCircuitBreaker = new CircuitBreaker({
    failureThreshold: 5,
    successThreshold: 3,
    timeout: 30000
});

// Helper para verificar se está expirado
const isExpired = (expirationDate) => {
    if (!expirationDate) return false;
    try {
        const expiry = expirationDate.toDate ? expirationDate.toDate() : new Date(expirationDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return expiry < today;
    } catch {
        return false;
    }
};

// Helper para formatar data
const formatDate = (dateValue) => {
    if (!dateValue) return null;
    try {
        const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
        return date.toLocaleDateString('pt-BR');
    } catch {
        return null;
    }
};

// Helper para verificar se é água
const isWaterMaterial = (material) => {
    if (!material) return false;
    const code = material.code || material.rawMaterialCode || '';
    const WATER_MATERIALS = ['MP001', 'AGUA001', 'WATER001'];
    return WATER_MATERIALS.includes(code.toUpperCase());
};

// Cache para localizações
let locationsCache = null;
let locationsCacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000;

/**
 * StockMovementModal - Versão com Seleção Explícita de Lote
 */
const StockMovementModal = ({ isOpen, onClose, selectedItem, onTransferComplete }) => {
    const { t } = useTranslation();

    // ── State ──────────────────────────────────────────────────────────────────
    const [allLocations, setAllLocations] = useState([]);
    const [locationsLoading, setLocationsLoading] = useState(false);
    const [sourceBatch, setSourceBatch] = useState(null);
    const [sourceLocationId, setSourceLocationId] = useState('');
    const [destinationLocationId, setDestinationLocationId] = useState('');
    const [quantity, setQuantity] = useState('');
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState({ message: '', type: '' });
    const [showBatchSelector, setShowBatchSelector] = useState(true);

    // Estado para lotes disponíveis
    const [availableBatches, setAvailableBatches] = useState([]);
    const [batchesLoading, setBatchesLoading] = useState(false);

    // Estado para saldo em tempo real
    const [realtimeSourceBalance, setRealtimeSourceBalance] = useState(0);
    const [realtimeSourceDocId, setRealtimeSourceDocId] = useState(null);

    // Estado para feedback visual de sucesso
    const [showSuccessCheck, setShowSuccessCheck] = useState(false);

    // Estado para retry count
    const [retryCount, setRetryCount] = useState(0);
    const [isRetrying, setIsRetrying] = useState(false);

    // ── Auth ───────────────────────────────────────────────────────────────────
    const { currentUser } = useAuth();
    const USER_EMAIL = currentUser ? currentUser.email : 'sistema';

    // ── Fetch locations with cache ─────────────────────────────────────────────
    const fetchLocations = useCallback(async () => {
        const now = Date.now();
        if (locationsCache && locationsCacheTimestamp &&
            (now - locationsCacheTimestamp) < CACHE_DURATION) {
            setAllLocations(locationsCache);
            return;
        }

        setLocationsLoading(true);
        try {
            const snap = await getDocs(collection(db, LOCATIONS_COLLECTION));
            const list = snap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .sort((a, b) => (a.description || '').localeCompare(b.description || ''));

            locationsCache = list;
            locationsCacheTimestamp = now;
            setAllLocations(list);
        } catch (err) {
            console.error('Erro ao buscar localizações:', err);
            setStatus({ message: t('pages.stock.movement.locationsLoadError', 'Erro ao carregar localizações. Tente novamente.'), type: 'error' });
        } finally {
            setLocationsLoading(false);
        }
    }, [t]);

    // ── Fetch available batches for the selected item ─────────────────────────
    useEffect(() => {
        if (!isOpen || !selectedItem) {
            setAvailableBatches([]);
            setSourceBatch(null);
            return;
        }

        const { rawMaterialId } = selectedItem;
        setBatchesLoading(true);

        const stockQuery = query(
            collection(db, CURRENT_STOCK_COLLECTION),
            where("rawMaterialId", "==", rawMaterialId),
            where("quantity", ">", 0.001)
        );

        const unsubscribe = onSnapshot(stockQuery, (snapshot) => {
            const batchesMap = new Map();

            snapshot.docs.forEach(doc => {
                const data = doc.data();
                const batchKey = data.lotNumber || 'NO_LOT';

                if (!batchesMap.has(batchKey)) {
                    batchesMap.set(batchKey, {
                        lotNumber: data.lotNumber || null,
                        expirationDate: data.expirationDate || null,
                        manufacturerDate: data.manufacturerDate || null,
                        totalQuantity: 0,
                        locations: [],
                        isWater: isWaterMaterial(data) || false
                    });
                }

                const batch = batchesMap.get(batchKey);
                batch.totalQuantity += data.quantity;
                batch.locations.push({
                    locationId: data.locationId,
                    quantity: data.quantity,
                    stockId: doc.id,
                    locationDescription: data.locationDescription,
                    expirationDate: data.expirationDate,
                    stockData: data
                });
            });

            const batches = Array.from(batchesMap.values());

            // Sort: non-expired first, then by lot number
            batches.sort((a, b) => {
                const aExpired = a.expirationDate && isExpired(a.expirationDate);
                const bExpired = b.expirationDate && isExpired(b.expirationDate);
                if (aExpired && !bExpired) return 1;
                if (!aExpired && bExpired) return -1;
                return (a.lotNumber || '').localeCompare(b.lotNumber || '');
            });

            setAvailableBatches(batches);
            setBatchesLoading(false);

            // Auto-select if only one batch
            if (batches.length === 1 && !sourceBatch) {
                setSourceBatch(batches[0]);
                setShowBatchSelector(false);
            }
        }, (error) => {
            console.error('Erro ao buscar lotes:', error);
            setBatchesLoading(false);
            setStatus({ message: t('pages.stock.movement.batchesLoadError', 'Erro ao carregar lotes. Tente novamente.'), type: 'error' });
        });

        return () => unsubscribe();
    }, [isOpen, selectedItem, sourceBatch, t]);

    // ── Real-time subscription to source stock ─────────────────────────────────
    useEffect(() => {
        if (!isOpen || !selectedItem || !sourceBatch || !sourceLocationId) {
            setRealtimeSourceDocId(null);
            return;
        }

        const batchLocation = sourceBatch.locations.find(l => l.locationId === sourceLocationId);

        if (!batchLocation) return;

        const sourceRef = doc(db, CURRENT_STOCK_COLLECTION, batchLocation.stockId);
        setRealtimeSourceDocId(batchLocation.stockId);

        const unsubscribe = onSnapshot(sourceRef,
            (docSnap) => {
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setRealtimeSourceBalance(data.quantity || 0);
                } else {
                    setRealtimeSourceBalance(0);
                }
            },
            (error) => {
                console.error('Erro no snapshot em tempo real:', error);
            }
        );

        return () => unsubscribe();
    }, [isOpen, selectedItem, sourceBatch, sourceLocationId]);

    // ── Fetch locations when modal opens ──────────────────────────────────────
    useEffect(() => {
        if (!isOpen) return;
        fetchLocations();
    }, [isOpen, fetchLocations]);

    // ── Reset on close ─────────────────────────────────────────────────────────
    const handleClose = useCallback(() => {
        setSourceBatch(null);
        setSourceLocationId('');
        setDestinationLocationId('');
        setQuantity('');
        setNotes('');
        setStatus({ message: '', type: '' });
        setShowSuccessCheck(false);
        setShowBatchSelector(true);
        setRealtimeSourceBalance(0);
        setRetryCount(0);
        setIsRetrying(false);
        onClose();
    }, [onClose]);

    // ── Derived: source balance ────────────────────────────────────────────────
    const sourceBalance = useMemo(() => {
        if (!sourceBatch || !sourceLocationId) return 0;

        const location = sourceBatch.locations.find(l => l.locationId === sourceLocationId);
        const balance = location?.quantity || 0;

        if (realtimeSourceDocId && realtimeSourceBalance !== undefined) {
            return realtimeSourceBalance;
        }
        return balance;
    }, [sourceBatch, sourceLocationId, realtimeSourceBalance, realtimeSourceDocId]);

    // ── Location options for source (within selected batch) ────────────────────
    const sourceLocationOptions = useMemo(() => {
        if (!sourceBatch) return [];
        return sourceBatch.locations
            .map(loc => ({
                id: loc.locationId,
                description: loc.locationDescription,
                quantity: loc.quantity,
                isExpired: loc.expirationDate ? isExpired(loc.expirationDate) : false
            }))
            .sort((a, b) => a.id.localeCompare(b.id));
    }, [sourceBatch]);

    // ── Destination options: all locations EXCEPT the source ──────────────────
    const destinationOptions = useMemo(() => {
        return allLocations.filter(l => l.id !== sourceLocationId);
    }, [allLocations, sourceLocationId]);

    // ── Validação on-the-fly da quantidade ─────────────────────────────────────
    const quantityValidation = useMemo(() => {
        const parsedQty = parseFloat(quantity);

        if (!quantity || quantity === '') {
            return { isValid: true, message: '' };
        }

        if (isNaN(parsedQty) || parsedQty <= 0) {
            return { isValid: false, message: t('pages.stock.movement.quantityGreaterThanZero', 'Quantidade deve ser maior que zero') };
        }

        if (parsedQty > sourceBalance) {
            return {
                isValid: false,
                message: t('pages.stock.movement.quantityExceedsBalance', 'Quantidade excede o saldo disponível ({balance})', { balance: sourceBalance.toFixed(3) })
            };
        }

        if (parsedQty < 0.001 && parsedQty > 0) {
            return { isValid: false, message: t('pages.stock.movement.minimumQuantity', 'Quantidade mínima: 0.001') };
        }

        return { isValid: true, message: '' };
    }, [quantity, sourceBalance, t]);

    // ── Get location description helper ───────────────────────────────────────
    const getLocationDescription = (locationId) => {
        const location = allLocations.find(l => l.id === locationId);
        return location?.description || locationId;
    };

    // ── Handle batch selection ─────────────────────────────────────────────────
    const handleBatchSelect = (batch) => {
        setSourceBatch(batch);
        setSourceLocationId('');
        setDestinationLocationId('');
        setQuantity('');
        setShowBatchSelector(false);
        setStatus({ message: '', type: '' });
    };

    // ── Verificar consistência pós-transferência ──────────────────────────────
    const verifyTransferConsistency = async (rawMaterialId, lotNumber) => {
        try {
            const stockQuery = query(
                collection(db, CURRENT_STOCK_COLLECTION),
                where("rawMaterialId", "==", rawMaterialId),
                ...(lotNumber ? [where("lotNumber", "==", lotNumber)] : [])
            );

            const snapshot = await getDocs(stockQuery);
            let totalQuantity = 0;
            const locations = [];

            snapshot.forEach(doc => {
                const data = doc.data();
                totalQuantity += data.quantity || 0;
                locations.push({
                    id: doc.id,
                    locationId: data.locationId,
                    quantity: data.quantity
                });
            });

            return {
                totalQuantity,
                locations,
                count: snapshot.size,
                isConsistent: totalQuantity >= 0
            };
        } catch (error) {
            console.error('Erro ao verificar consistência:', error);
            return null;
        }
    };

    // ── Save: atomic Firestore transaction with lot preservation ──────────────
    const performTransfer = async (retryAttempt = 0) => {
        const parsedQty = parseFloat(quantity);

        try {
            const { rawMaterialId, rawMaterialCode, rawMaterialName, isProduct } = selectedItem;
            const batchLocation = sourceBatch.locations.find(l => l.locationId === sourceLocationId);

            if (!batchLocation) {
                throw new Error(t('pages.stock.movement.locationNotFoundInBatch', 'Localização {location} não encontrada no lote selecionado.', { location: sourceLocationId }));
            }

            const sourceStockId = batchLocation.stockId;
            const sourceRef = doc(db, CURRENT_STOCK_COLLECTION, sourceStockId);

            // Build destination stock ID with lot number
            let destStockId;
            if (isProduct && sourceBatch.lotNumber) {
                destStockId = `${rawMaterialId}_${destinationLocationId}_${sourceBatch.lotNumber}`;
            } else {
                destStockId = `${rawMaterialId}_${destinationLocationId}`;
            }
            const destRef = doc(db, CURRENT_STOCK_COLLECTION, destStockId);

            const outflowsRef = collection(db, STOCK_OUTFLOWS_COLLECTION);
            const entriesRef = collection(db, STOCK_ENTRIES_COLLECTION);

            const destLocationDescription = getLocationDescription(destinationLocationId);
            const sourceLocationDescription = getLocationDescription(sourceLocationId);

            // Verificar se o destino está ativo/desativado
            const destLocation = allLocations.find(l => l.id === destinationLocationId);
            if (destLocation && destLocation.active === false) {
                throw new Error(t('pages.stock.movement.destinationInactive', 'Localização de destino está desativada. Não é possível transferir.'));
            }

            // Run the transaction
            const result = await runTransaction(db, async (transaction) => {
                const [sourceSnap, destSnap] = await Promise.all([
                    transaction.get(sourceRef),
                    transaction.get(destRef),
                ]);

                if (!sourceSnap.exists()) {
                    throw new Error(t('pages.stock.movement.sourceNotFound', 'Documento de estoque de origem não encontrado ({id}).', { id: sourceStockId }));
                }

                const sourceData = sourceSnap.data();
                const currentSourceQty = sourceData.quantity || 0;

                if (parsedQty > currentSourceQty) {
                    throw new Error(
                        t('pages.stock.movement.insufficientBalance', 'Saldo insuficiente na origem. Disponível: {available}, solicitado: {requested}.', {
                            available: currentSourceQty.toFixed(3),
                            requested: parsedQty.toFixed(3)
                        })
                    );
                }

                const newSourceQty = currentSourceQty - parsedQty;
                const currentDestQty = destSnap.exists() ? (destSnap.data().quantity || 0) : 0;
                const newDestQty = currentDestQty + parsedQty;

                const now = serverTimestamp();
                const transferNotes = notes.trim() || t('pages.stock.movement.defaultTransferNote', 'Transferência de {source} para {destination}', {
                    source: sourceLocationId,
                    destination: destinationLocationId
                });

                // Extract lot information from source batch
                const lotNumber = sourceBatch.lotNumber || null;
                const expirationDate = sourceBatch.expirationDate || null;
                const manufacturerDate = sourceBatch.manufacturerDate || null;

                // Update source stock (delete if zero)
                if (newSourceQty <= 0.001) {
                    transaction.delete(sourceRef);
                } else {
                    transaction.update(sourceRef, {
                        quantity: newSourceQty,
                        updatedAt: now,
                    });
                }

                // Update or create destination stock with lot information
                if (destSnap.exists()) {
                    transaction.update(destRef, {
                        quantity: newDestQty,
                        updatedAt: now,
                        ...(lotNumber !== null && { lotNumber }),
                        ...(expirationDate !== null && { expirationDate }),
                        ...(manufacturerDate !== null && { manufacturerDate }),
                    });
                } else {
                    transaction.set(destRef, {
                        rawMaterialId,
                        rawMaterialCode,
                        rawMaterialName,
                        locationId: destinationLocationId,
                        locationDescription: destLocationDescription,
                        quantity: newDestQty,
                        createdAt: now,
                        updatedAt: now,
                        lotNumber: lotNumber,
                        expirationDate: expirationDate,
                        manufacturerDate: manufacturerDate,
                        isProduct: isProduct || false,
                    });
                }

                // Record TRANSFER_OUT in stock-outflows (with lot info)
                const outflowRef = doc(outflowsRef);
                transaction.set(outflowRef, {
                    rawMaterialId,
                    rawMaterialCode,
                    rawMaterialName,
                    locationIds: [sourceLocationId],
                    locationQuantities: { [sourceLocationId]: parsedQty },
                    quantity: parsedQty,
                    type: 'TRANSFER_OUT',
                    destinationLocationId,
                    destinationLocationDescription: destLocationDescription,
                    notes: transferNotes,
                    createdAt: now,
                    createdBy: USER_EMAIL,
                    lotNumber: lotNumber,
                    expirationDate: expirationDate,
                    manufacturerDate: manufacturerDate,
                    isProduct: isProduct || false,
                    transferId: `${Date.now()}_${Math.random().toString(36).substr(2, 6)}`
                });

                // Record TRANSFER_IN in stock-entries (with lot info)
                const entryRef = doc(entriesRef);
                transaction.set(entryRef, {
                    rawMaterialId,
                    rawMaterialCode,
                    rawMaterialName,
                    locationIds: [destinationLocationId],
                    locationQuantities: { [destinationLocationId]: parsedQty },
                    quantity: parsedQty,
                    type: 'TRANSFER_IN',
                    sourceLocationId,
                    sourceLocationDescription: sourceLocationDescription,
                    notes: transferNotes,
                    createdAt: now,
                    createdBy: USER_EMAIL,
                    lotNumber: lotNumber,
                    expirationDate: expirationDate,
                    manufacturerDate: manufacturerDate,
                    isProduct: isProduct || false,
                    transferId: `${Date.now()}_${Math.random().toString(36).substr(2, 6)}`
                });

                return {
                    sourceStockId,
                    destStockId,
                    oldSourceQty: currentSourceQty,
                    newSourceQty,
                    oldDestQty: currentDestQty,
                    newDestQty,
                    transferredQty: parsedQty,
                    lotNumber,
                    isNewDest: !destSnap.exists()
                };
            });

            // Verify consistency after transfer
            const verification = await verifyTransferConsistency(rawMaterialId, sourceBatch.lotNumber);

            if (verification && !verification.isConsistent) {
                console.warn('⚠️ Inconsistência detectada após transferência:', verification);
                setStatus({
                    message: t('pages.stock.movement.inconsistencyWarning', 'Transferência realizada, mas detectada inconsistência. Saldo total: {balance}', {
                        balance: verification.totalQuantity.toFixed(3)
                    }),
                    type: 'warning'
                });
            }

            // Success!
            setShowSuccessCheck(true);
            setStatus({
                message: t('pages.stock.movement.transferSuccess', '✅ Transferência realizada com sucesso! Lote: {lot}', {
                    lot: sourceBatch.lotNumber || t('pages.stock.movement.noLot', 'Sem lote')
                }),
                type: 'success'
            });

            // Callback for parent component
            if (onTransferComplete) {
                onTransferComplete({
                    ...result,
                    transferredQty: parsedQty,
                    notes: notes.trim()
                });
            }

            setTimeout(() => {
                handleClose();
            }, 1500);

            return result;

        } catch (error) {
            // Check if this is a retryable error
            const isRetryable =
                error.message?.includes('network') ||
                error.message?.includes('timeout') ||
                error.message?.includes('unavailable') ||
                error.message?.includes('deadline') ||
                error.message?.includes('resource exhausted') ||
                error.code === 'unavailable' ||
                error.code === 'deadline-exceeded';

            if (isRetryable && retryAttempt < 3) {
                const delay = Math.pow(2, retryAttempt) * 1000;
                setRetryCount(retryAttempt + 1);
                setIsRetrying(true);
                setStatus({
                    message: t('pages.stock.movement.retryAttempt', '⚠️ Falha temporária. Tentando novamente em {delay}s... (Tentativa {attempt}/3)', {
                        delay: (delay / 1000).toFixed(1),
                        attempt: retryAttempt + 1
                    }),
                    type: 'warning'
                });

                await new Promise(resolve => setTimeout(resolve, delay));
                setIsRetrying(false);
                return await performTransfer(retryAttempt + 1);
            }

            throw error;
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setStatus({ message: '', type: '' });

        const parsedQty = parseFloat(quantity);

        if (!sourceBatch) {
            setStatus({ message: t('pages.stock.movement.selectBatch', 'Selecione o lote de origem.'), type: 'error' });
            return;
        }
        if (!sourceLocationId) {
            setStatus({ message: t('pages.stock.movement.selectSource', 'Selecione a localização de origem.'), type: 'error' });
            return;
        }
        if (!destinationLocationId) {
            setStatus({ message: t('pages.stock.movement.selectDestination', 'Selecione a localização de destino.'), type: 'error' });
            return;
        }
        if (sourceLocationId === destinationLocationId) {
            setStatus({ message: t('pages.stock.movement.sameLocation', 'Origem e destino não podem ser iguais.'), type: 'error' });
            return;
        }
        if (!quantityValidation.isValid) {
            setStatus({ message: quantityValidation.message, type: 'error' });
            return;
        }

        setLoading(true);
        setRetryCount(0);
        setIsRetrying(false);

        try {
            await transferCircuitBreaker.execute(async () => {
                return await performTransfer(0);
            });
        } catch (error) {
            console.error('Erro na transferência de estoque:', error);

            if (transferCircuitBreaker.state === 'OPEN') {
                setStatus({
                    message: t('pages.stock.movement.circuitBreakerOpen', '⛔ Sistema temporariamente indisponível para transferências. Tente novamente em alguns minutos.'),
                    type: 'error'
                });
            } else {
                setStatus({ message: t('pages.stock.movement.transferError', '❌ Erro: {message}', { message: error.message }), type: 'error' });
            }

            const errorElement = document.querySelector(`.${styles.alert}.${styles.error}`);
            if (errorElement) {
                errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        } finally {
            setLoading(false);
            setIsRetrying(false);
        }
    };

    // ── Render Content ─────────────────────────────────────────────────────────
    const renderContent = () => {
        if (batchesLoading) {
            return (
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}>⏳</div>
                        <h3 className={styles.sectionTitle}>{t('pages.stock.movement.loadingBatches', 'Carregando lotes disponíveis...')}</h3>
                    </div>
                    <div className={styles.infoTextSmall} style={{ padding: '10px', textAlign: 'center' }}>
                        <span className={styles.spinner}></span> {t('pages.stock.movement.checkingStock', 'Verificando estoque por lote...')}
                    </div>
                </div>
            );
        }

        if (showBatchSelector) {
            return (
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}>🏷️</div>
                        <h3 className={styles.sectionTitle}>{t('pages.stock.movement.batchSelection', 'Seleção de Lote')}</h3>
                        <span className={styles.sectionBadge}>{t('common.required', 'Obrigatório')}</span>
                    </div>
                    <div className={styles.formGrid}>
                        <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                            <label>{t('pages.stock.movement.selectBatchForTransfer', 'Selecione o Lote para transferência:')}</label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                                {availableBatches.length === 0 ? (
                                    <div className={styles.warningTextSmall} style={{ padding: '10px', background: 'var(--warning-light)', borderRadius: '8px' }}>
                                        ⚠️ {t('pages.stock.movement.noBatchAvailable', 'Nenhum lote com estoque disponível para este item.')}
                                    </div>
                                ) : (
                                    availableBatches.map((batch, idx) => {
                                        const isBatchExpired = batch.expirationDate && isExpired(batch.expirationDate);
                                        return (
                                            <div
                                                key={idx}
                                                onClick={() => handleBatchSelect(batch)}
                                                className={styles.batchOption}
                                                style={{
                                                    padding: '12px',
                                                    border: `2px solid ${isBatchExpired ? '#e53e3e' : 'var(--border-color)'}`,
                                                    borderRadius: '8px',
                                                    cursor: 'pointer',
                                                    backgroundColor: isBatchExpired ? '#fff5f5' : 'var(--content-bg)',
                                                    transition: 'all 0.2s ease',
                                                    opacity: isBatchExpired ? 0.7 : 1
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!isBatchExpired) {
                                                        e.currentTarget.style.borderColor = 'var(--primary-color)';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.borderColor = isBatchExpired ? '#e53e3e' : 'var(--border-color)';
                                                }}
                                            >
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                                                    <div>
                                                        <strong style={{ fontSize: '1rem' }}>
                                                            {batch.lotNumber || t('pages.stock.movement.noLotNumber', 'Sem Número de Lote')}
                                                        </strong>
                                                        {batch.expirationDate && (
                                                            <span style={{
                                                                marginLeft: '12px',
                                                                fontSize: '0.85rem',
                                                                color: isBatchExpired ? '#c62828' : '#2e7d32'
                                                            }}>
                                                                {t('pages.stock.movement.expirationLabel', 'Validade: {date}', { date: formatDate(batch.expirationDate) })}
                                                                {isBatchExpired && ` ⚠️ ${t('pages.stock.movement.expired', 'VENCIDO')}`}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div style={{ fontWeight: 'bold', color: 'var(--primary-color)' }}>
                                                        {t('pages.stock.movement.totalLabel', 'Total: {qty} kg/L/un', { qty: batch.totalQuantity.toFixed(3) })}
                                                    </div>
                                                </div>
                                                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                                                    {t('pages.stock.movement.locationsLabel', 'Localizações: {locations}', { locations: batch.locations.map(l => l.locationId).join(', ') })}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <>
                {/* Lote Selecionado */}
                {sourceBatch && (
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}>✅</div>
                            <h3 className={styles.sectionTitle}>{t('pages.stock.movement.selectedBatch', 'Lote Selecionado')}</h3>
                        </div>
                        <div style={{
                            padding: '12px',
                            backgroundColor: 'var(--success-light)',
                            borderRadius: '8px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: '8px'
                        }}>
                            <div>
                                <strong>{t('pages.stock.movement.batchLabel', 'Lote:')} </strong>
                                <span style={{ fontWeight: 'bold' }}>
                                    {sourceBatch.lotNumber || t('pages.stock.movement.noLotNumber', 'Sem Número de Lote')}
                                </span>
                                {sourceBatch.expirationDate && (
                                    <span style={{ marginLeft: '12px', fontSize: '0.85rem' }}>
                                        {t('pages.stock.movement.expirationLabel', 'Validade: {date}', { date: formatDate(sourceBatch.expirationDate) })}
                                        {isExpired(sourceBatch.expirationDate) && ` ⚠️ ${t('pages.stock.movement.expired', 'VENCIDO')}`}
                                    </span>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowBatchSelector(true)}
                                className={styles.actionButton}
                                disabled={loading}
                            >
                                {t('pages.stock.movement.changeBatch', 'Trocar Lote')}
                            </button>
                        </div>
                    </div>
                )}

                {/* Origem */}
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}>📤</div>
                        <h3 className={styles.sectionTitle}>{t('pages.stock.movement.sourceLocation', 'Localização de Origem')}</h3>
                        <span className={styles.sectionBadge}>{t('common.required', 'Obrigatório')}</span>
                    </div>
                    <div className={styles.formGrid}>
                        <div className={styles.inputGroup}>
                            <label>
                                {t('pages.stock.movement.sourceLabel', 'Origem')} <span className={styles.required}>*</span>
                            </label>
                            <select
                                value={sourceLocationId}
                                onChange={(e) => {
                                    setSourceLocationId(e.target.value);
                                    setQuantity('');
                                }}
                                className={`${styles.input} ${styles.select}`}
                                disabled={loading}
                                required
                            >
                                <option value="">{t('pages.stock.movement.selectSourceOption', 'Selecione a origem...')}</option>
                                {sourceLocationOptions.map(loc => {
                                    const isExpiredBatch = sourceBatch.expirationDate && isExpired(sourceBatch.expirationDate);
                                    return (
                                        <option key={loc.id} value={loc.id}>
                                            {loc.id} — {loc.description}
                                            {isExpiredBatch ? ` ⚠️ ${t('pages.stock.movement.batchExpired', 'LOTE VENCIDO')}` : ''}
                                            {loc.isExpired ? ` ⚠️ ${t('pages.stock.movement.expired', 'VENCIDO')}` : ''}
                                            {` - ${t('pages.stock.movement.balanceLabel', 'Saldo: {qty}', { qty: loc.quantity.toFixed(3) })}`}
                                        </option>
                                    );
                                })}
                            </select>

                            {sourceLocationId && (
                                <div className={styles.stockInfoContainer} style={{
                                    marginTop: '12px',
                                    padding: '12px',
                                    background: sourceBalance > 0 ? 'var(--success-light)' : 'var(--danger-light)',
                                    borderRadius: 'var(--border-radius-md)',
                                    borderLeft: `4px solid ${sourceBalance > 0 ? 'var(--success-color)' : 'var(--danger-color)'}`
                                }}>
                                    <div className={styles.currentStockInfo}>
                                        <span className={styles.stockBadge}>
                                            <strong>{t('pages.stock.movement.availableBalance', 'Saldo disponível na origem:')}</strong>
                                            <span style={{
                                                fontSize: '1.2rem',
                                                fontWeight: 'bold',
                                                color: sourceBalance > 0 ? 'var(--success-color)' : 'var(--danger-color)'
                                            }}>
                                                {sourceBalance.toFixed(3)}
                                            </span>
                                            {realtimeSourceDocId && (
                                                <span style={{ fontSize: '0.7rem', color: '#666', marginLeft: '8px' }}>
                                                    ({t('pages.stock.movement.realTime', 'tempo real')})
                                                </span>
                                            )}
                                        </span>
                                        {sourceBatch.lotNumber && (
                                            <div style={{ fontSize: '0.8rem', marginTop: '4px', color: '#666' }}>
                                                {t('pages.stock.movement.batchLabel', 'Lote:')} {sourceBatch.lotNumber}
                                                {sourceBatch.expirationDate && ` | ${t('pages.stock.movement.expirationLabel', 'Validade: {date}', { date: formatDate(sourceBatch.expirationDate) })}`}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Destino */}
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}>📥</div>
                        <h3 className={styles.sectionTitle}>{t('pages.stock.movement.destinationLocation', 'Localização de Destino')}</h3>
                        <span className={styles.sectionBadge}>{t('common.required', 'Obrigatório')}</span>
                    </div>
                    <div className={styles.formGrid}>
                        <div className={styles.inputGroup}>
                            <label>
                                {t('pages.stock.movement.destinationLabel', 'Destino')} <span className={styles.required}>*</span>
                            </label>
                            <select
                                value={destinationLocationId}
                                onChange={(e) => setDestinationLocationId(e.target.value)}
                                className={`${styles.input} ${styles.select}`}
                                disabled={loading || !sourceLocationId}
                                required
                            >
                                <option value="">{t('pages.stock.movement.selectDestinationOption', 'Selecione o destino...')}</option>
                                {destinationOptions.map(loc => (
                                    <option key={loc.id} value={loc.id}>
                                        {loc.id} — {loc.description}
                                        {loc.active === false && ` (${t('pages.stock.movement.inactive', 'Inativa')})`}
                                    </option>
                                ))}
                            </select>
                            <small className={styles.fieldHint}>
                                {t('pages.stock.movement.destinationHint', 'Local para onde o estoque será transferido')}
                            </small>
                        </div>
                    </div>
                </div>

                {/* Quantidade */}
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}>🔢</div>
                        <h3 className={styles.sectionTitle}>{t('pages.stock.movement.quantitySection', 'Quantidade')}</h3>
                        <span className={styles.sectionBadge}>{t('common.required', 'Obrigatório')}</span>
                    </div>
                    <div className={styles.formGrid}>
                        <div className={styles.inputGroup}>
                            <label>
                                {t('pages.stock.movement.quantityToMove', 'Quantidade a Movimentar')} <span className={styles.required}>*</span>
                            </label>
                            <input
                                type="number"
                                min="0.001"
                                step="0.001"
                                max={sourceBalance}
                                placeholder={t('pages.stock.movement.maxPlaceholder', 'Máx: {max}', { max: sourceBalance > 0 ? sourceBalance.toFixed(3) : '0' })}
                                value={quantity}
                                onChange={(e) => setQuantity(e.target.value)}
                                className={`${styles.input} ${!quantityValidation.isValid && quantity ? styles.inputError : ''}`}
                                disabled={loading || !sourceLocationId}
                                required
                            />
                            {quantity && !quantityValidation.isValid && (
                                <small className={styles.errorTextSmall}>
                                    ⚠️ {quantityValidation.message}
                                </small>
                            )}
                            {quantity && quantityValidation.isValid && sourceBalance > 0 && (
                                <small className={styles.successTextSmall}>
                                    ✓ {t('pages.stock.movement.validQuantity', 'Quantidade válida')}
                                </small>
                            )}
                            <small className={styles.fieldHint}>
                                {t('pages.stock.movement.quantityHint', 'Digite a quantidade a ser transferida (máx: {max})', { max: sourceBalance > 0 ? sourceBalance.toFixed(3) : '0' })}
                            </small>
                        </div>
                    </div>
                </div>

                {/* Observações */}
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}>📝</div>
                        <h3 className={styles.sectionTitle}>{t('pages.stock.movement.notesSection', 'Observações')}</h3>
                        <span className={styles.sectionBadge}>{t('pages.stock.movement.optionalBadge', 'Opcional')}</span>
                    </div>
                    <div className={styles.formGrid}>
                        <div className={styles.inputGroup}>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                className={styles.textarea}
                                rows="3"
                                disabled={loading}
                                placeholder={t('pages.stock.movement.notesPlaceholder', 'Motivo da movimentação, ex: Reorganização de armazém, reposição de linha de produção...')}
                            />
                            <small className={styles.fieldHint}>
                                {t('pages.stock.movement.notesHint', 'Informações complementares sobre esta transferência')}
                            </small>
                        </div>
                    </div>
                </div>
            </>
        );
    };

    // ── Guard: don't render when closed ───────────────────────────────────────
    if (!isOpen || !selectedItem) return null;

    const isFormValid = () => {
        if (showBatchSelector || !sourceBatch) return false;
        if (!sourceLocationId || !destinationLocationId) return false;
        if (!quantity || !quantityValidation.isValid) return false;
        if (sourceBalance <= 0) return false;
        return true;
    };

    return (
        <FormModal
            isOpen={isOpen}
            onClose={handleClose}
            onSubmit={handleSave}
            isLoading={loading}
            error={status.type === 'error' ? status.message : ''}
            success={status.type === 'success' ? status.message : ''}
            title={t('pages.stock.movement.title', 'Transferência de Estoque')}
            subtitle={`${selectedItem.rawMaterialCode} — ${selectedItem.rawMaterialName}`}
            submitText={loading ? (isRetrying ? t('pages.stock.movement.retrying', 'Tentando novamente...') : t('pages.stock.movement.moving', 'Movimentando...')) : t('pages.stock.movement.moveButton', 'Movimentar')}
            maxWidth="600px"
        >
            {renderContent()}
        </FormModal>
    );
};

export default StockMovementModal;